import React, { useState } from 'react';
import { Header } from '../components/layout/Header';
import { Button } from '../components/shared/Button';
import { useTimerStore } from '../stores/timerStore';
import { useFirebaseSync, useFindRoomByCode } from '../hooks/useFirebaseSync';

import type { Direction } from '../hooks/useKonamiCode';

interface HomePageProps {
  onJoinRoom: (roomId: string) => void;
  onDirectionClick?: (dir: Direction) => void;
  progressLength?: number;
}

export function HomePage({ onJoinRoom, onDirectionClick, progressLength }: HomePageProps) {
  const { recentRooms } = useTimerStore();
  const { createRoom } = useFirebaseSync(null);
  const { findRoom } = useFindRoomByCode();

  const [roomName, setRoomName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const handleCreate = async () => {
    const name = roomName.trim() || 'Nouvelle room';
    setIsCreating(true);
    setError('');
    try {
      const roomId = await createRoom(name);
      onJoinRoom(roomId);
    } catch {
      setError('Erreur lors de la création de la room');
    }
    setIsCreating(false);
  };

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length < 4) {
      setError('Code trop court');
      return;
    }
    setIsJoining(true);
    setError('');
    try {
      const roomId = await findRoom(code);
      if (roomId) {
        onJoinRoom(roomId);
      } else {
        setError('Aucune room trouvée avec ce code');
      }
    } catch {
      setError('Erreur lors de la recherche');
    }
    setIsJoining(false);
  };

  return (
    <div className="min-h-screen bg-namm-bg">
      <Header onDirectionClick={onDirectionClick} progressLength={progressLength} />

      <main className="max-w-2xl mx-auto px-5 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-namm-cyan text-xs font-mono uppercase tracking-[0.14em] mb-6">
            <span className="w-8 h-px bg-namm-cyan" />
            Session Timer Hub
            <span className="w-8 h-px bg-namm-cyan" />
          </div>
          <h1 className="font-heading text-5xl sm:text-7xl uppercase tracking-tighter text-namm-text mb-4">
            namm
          </h1>
          <p className="text-namm-muted font-mono text-sm max-w-md mx-auto">
            Chronomètres collaboratifs en temps réel. Créez une room, partagez le code, synchronisez vos sessions.
          </p>
        </div>

        {/* Actions */}
        <div className="grid sm:grid-cols-2 gap-5 mb-12">
          {/* Create room */}
          <div className="border border-namm-line/40 bg-namm-panel/70 p-6 relative">
            <div className="absolute top-3 right-3 w-8 h-px bg-namm-cyan" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-namm-cyan">
              Créer
            </span>
            <h2 className="font-heading text-xl uppercase tracking-tight text-namm-text mt-3 mb-4">
              Nouvelle Room
            </h2>
            <input
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Nom de la room..."
              className="w-full bg-namm-panel-soft border border-namm-line px-4 py-2.5 text-sm font-mono text-namm-text outline-none focus:border-namm-cyan placeholder:text-namm-muted/50 mb-4"
              maxLength={40}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <Button
              variant="primary"
              className="w-full"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? '...' : 'Créer la room'}
            </Button>
          </div>

          {/* Join room */}
          <div className="border border-namm-line/40 bg-namm-panel/70 p-6">
            <span className="text-[10px] font-mono uppercase tracking-widest text-namm-cyan">
              Rejoindre
            </span>
            <h2 className="font-heading text-xl uppercase tracking-tight text-namm-text mt-3 mb-4">
              Entrer un Code
            </h2>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              className="w-full bg-namm-panel-soft border border-namm-line px-4 py-2.5 text-sm font-mono text-namm-text outline-none focus:border-namm-cyan placeholder:text-namm-muted/50 mb-4 uppercase tracking-widest text-center"
              maxLength={6}
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            />
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleJoin}
              disabled={isJoining}
            >
              {isJoining ? '...' : 'Rejoindre'}
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="text-center text-xs font-mono text-namm-danger mb-6 py-2 border border-namm-danger/30 bg-namm-danger/5">
            {error}
          </div>
        )}

        {/* Recent rooms */}
        {recentRooms.length > 0 && (
          <div>
            <h3 className="text-[10px] font-mono uppercase tracking-widest text-namm-muted mb-3">
              Rooms récentes
            </h3>
            <div className="space-y-2">
              {recentRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => onJoinRoom(room.id)}
                  className="w-full flex items-center justify-between py-3 px-4 border border-namm-line/30 bg-namm-panel-soft/50 hover:border-namm-cyan/40 transition-all group text-left"
                >
                  <span className="text-sm font-mono text-namm-text group-hover:text-namm-cyan transition-colors">
                    {room.name}
                  </span>
                  <span className="text-xs font-mono text-namm-muted tracking-widest">
                    [{room.code}]
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

